import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CoursesService } from '@draeger-academy/common';
import { ConfigurationService } from '@draeger-academy/common/lib/services/configuration/configuration.service';
import { TranslateService } from '@ngx-translate/core';
import { Course } from 'models/course';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { AcsInputComponent } from '../acs-input/acs-input.component';

@Component({
    selector: 'da-filtering',
    templateUrl: './filtering.component.html',
    styleUrls: ['./filtering.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilteringComponent implements OnInit, OnChanges {
    @Input() showSecondLevelFiltering = false;
    @Input() hideFirstLevelFiltering = false;
    @Input() filterName = '';
    @Input() showThirdLevelFiltering = false;
    @Input() categories: any[];
    @Input() isUserPage: boolean;
    @Input() filterResetDone: boolean;
    // change data to use getter and setter
    @Input() saveFilteringParams = false;
    @Input() coursesByTitle: Course[];
    // tslint:disable-next-line:no-output-native
    @Output()
    change: EventEmitter<string> = new EventEmitter<string>();
    @Output()
    changeSubCat: EventEmitter<string> = new EventEmitter<string>();
    @Output()
    changeSubSubCat: EventEmitter<string> = new EventEmitter<string>();
    @Output()
    titleValue: EventEmitter<string> = new EventEmitter<string>();
    @Input() hasCourses = false;
    @Input() showTitleSearch = true;
    @Input() setPreselectedSubCat;
    @Input() setPreselectedSubSubCat;
    public selectedValue = '';
    public selectedValueSubCat = '';
    public selectedValueSubSubCat = '';
    public allDataFatched = false;
    private _$translations: BehaviorSubject<any> = new BehaviorSubject({});
    public hasFocus = false;
    public hasFocusSubCat = false;
    public hasFocusSubSubCat = false;
    public hasFocusTitle = false;
    public title = '';
    public disablingBusinessField: boolean;
    private _subscriptionQueryParams: Subscription;
    private _$categories: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private _$showFiltering: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    @ViewChild(AcsInputComponent) inputField: AcsInputComponent;
    public label;
    public labelNoValue;

    public get categories$(): Observable<any> {
        return this._$categories.asObservable();
    }

    private _$subCategories: BehaviorSubject<{}> = new BehaviorSubject<{}>(null);

    public get subCategories$(): Observable<{}> {
        return this._$subCategories.asObservable();
    }

    private _$subSubCategories: BehaviorSubject<{}> = new BehaviorSubject<{}>(null);

    public get subSubCategories$(): Observable<{}> {
        return this._$subSubCategories.asObservable();
    }

    public get showFiltering$(): Observable<boolean> {
        return this._$showFiltering.asObservable();
    }

    private _$disablingBusinessField: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public get disablingBusinessField$(): Observable<boolean> {
        return this._$disablingBusinessField.asObservable();
    }

    constructor(
        private activatedRoute: ActivatedRoute,
        public config: ConfigurationService,
        private _coursesService: CoursesService,
        private _translateService: TranslateService
    ) {}

    ngOnInit() {
        this.initFiltering();
        this._coursesService.languageSelected.subscribe((language) => {
            setTimeout(() => {
                this.initFiltering();
            }, 500);
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['categories']) {
            this._$categories.next(this.categories);
        }
        if (changes['filterResetDone']) {
            this.initFiltering();
        }
        if (changes['setPreselectedSubCat'] && changes['setPreselectedSubCat'].currentValue) {
            const setValue = changes['setPreselectedSubCat'].currentValue;
            if (this._matchPreselected(this._$subCategories.getValue(), setValue)) {
                this.selectedValueSubCat = setValue;
                this.onSelectSubCat(setValue, true);
            }
        }
        if (changes['setPreselectedSubSubCat'] && changes['setPreselectedSubSubCat'].currentValue) {
            const setValue = changes['setPreselectedSubSubCat'].currentValue;
            if (this._matchPreselected(this._$subSubCategories.getValue(), setValue)) {
                this.selectedValueSubSubCat = setValue;
                this.onSelectSubSubCat(setValue);
            }
        }
    }

    private _matchPreselected(list, value): boolean {
        return list.filter(x => x.name === value).length > 0
    }
    
    public initFiltering() {
        const _site = this.config.getCurrentSite();
        const currentPortal = this.config.portalId;
        const isPortal = this.config.isPartnerPage();
        this.bussinesFieldDisabled(isPortal);

        this.getLabelsForAllOrNothing();
        this.showFiltering();

        this._subscriptionQueryParams = this.activatedRoute.queryParams.subscribe((params) => {
            const _catFromParameters = params['cat'];
            if (_catFromParameters) {
                if (this._$categories.getValue()) {
                    this._$categories.getValue().filter((data) => {
                        if (data.name.toLowerCase().replaceAll(' ', '') === _catFromParameters.toLowerCase()) {
                            this.onSelect(data.id, false);
                            this.selectedValue = data.id;
                        }
                    });
                }
                this.initFilters(_site);
            } else {
                if (isPortal) {
                    this.initFilters(currentPortal);
                } else {
                    this.initFilters(_site);
                }

                this.getCategories();
            }
        });
    }

    public showFiltering() {
        const isPortal = this.config.isPartnerPage();

        if (!isPortal) {
            this._$showFiltering.next(this.config.hasFeature('SearchBy'));
        } else {
            this.config.getConfigPartnerByPortal().then((values) => {
                if (values) {
                    const tempFeatures = values.filter((setting) => setting.key.toLowerCase() === 'features');
                    let hasCheckPrerequest;
                    if (tempFeatures && tempFeatures[0] && tempFeatures[0].value && tempFeatures[0].value.length > 0) {
                        hasCheckPrerequest = tempFeatures[0].value.filter((setting) => setting.name === 'SearchBy');
                    }
                    if (hasCheckPrerequest && hasCheckPrerequest[0] && hasCheckPrerequest[0].enabled) {
                        this._$showFiltering.next(true);
                    }
                }
            });
        }
    }

    public valuechange(valueTyped) {
        const isPortal = this.config.isPartnerPage();
        const portalLanguage =
        JSON.parse(sessionStorage.getItem('portalLang')) && JSON.parse(sessionStorage.getItem('portalLang')).lang
            ? JSON.parse(sessionStorage.getItem('portalLang')).lang
            : '';
        const _site = isPortal ? this.config.portalId + portalLanguage : this.config.getSiteFromUrl().site;
        sessionStorage.setItem(`titleFilteringValue${_site}`, valueTyped);
        this.titleValue.emit(valueTyped);
    }

    initFilters(_site) {
        let lang = '';
        const isPortal = this.config.isPartnerPage();
        if (isPortal) {
            if (JSON.parse(sessionStorage.getItem('portalLang'))) {
                lang = JSON.parse(sessionStorage.getItem('portalLang')).lang ? JSON.parse(sessionStorage.getItem('portalLang')).lang : '';
            }
        } else {
            lang = _site.split('_')[0];
        }

        if (sessionStorage.getItem(`selectedValue${_site}${lang}`)) {
            this.selectedValue = sessionStorage.getItem(`selectedValue${_site}${lang}`);
            if (this.selectedValue === '0' && this._$categories.getValue()[0]) {
                this.selectedValue = this._$categories.getValue()[0].id;
                this.onSelect(this._$categories.getValue()[0].id, false);
            }
        } else {
            if (this._$categories.getValue() && this._$categories.getValue()[0]) {
                this.selectedValue = this._$categories.getValue()[0].id;
                this.onSelect(this._$categories.getValue()[0].id, false);
            }
        }

        if (
            sessionStorage.getItem(`selectedSubCatValue${_site}${lang}`) &&
            sessionStorage.getItem(`selectedSubCatValue${_site}${lang}`) !== 'null'
        ) {
            this.selectedValueSubCat = sessionStorage.getItem(`selectedSubCatValue${_site}${lang}`);
        } else {
            this.selectedValueSubCat = this.label;
        }

        if (
            sessionStorage.getItem(`selectedSubSubCatValue${_site}${lang}`) &&
            sessionStorage.getItem(`selectedSubSubCatValue${_site}${lang}`) !== 'null'
        ) {
            this.selectedValueSubSubCat = sessionStorage.getItem(`selectedSubSubCatValue${_site}${lang}`);
        } else {
            this.selectedValueSubSubCat = this.label;
        }
        if (this._$categories.getValue() && this._$categories.getValue().length === 1) {
            const _onlyCategoryID = this._$categories.getValue()[0].id;
            this.selectedValue = _onlyCategoryID;
            this.onSelect(_onlyCategoryID, true);
            this.selectedValueSubCat = this.label;
        } else if (this._$categories.getValue().length > 1) {
            this.selectedValue = '0';
            this.onSelect('0', true); // Alle
        }
    }

    public reset() {
        this.selectedValueSubCat = this.label;
        this.selectedValueSubSubCat = this.label;
        this.selectedValue = this._$categories.getValue()[0].id;
        this.inputField.reset();
    }

    public hasValue(fieldname) {
        return fieldname ? fieldname.toString() !== '' : false;
    }

    onSelect(categoryId: string, reset: boolean) {
        this.removeParam('cat');

        const _site = this.config.getCurrentSite();
        const currentPortal = this.config.portalId;
        const isPortal = this.config.isPartnerPage();
        let lang = '';
        if (JSON.parse(sessionStorage.getItem('portalLang'))) {
            lang = JSON.parse(sessionStorage.getItem('portalLang')).lang ? JSON.parse(sessionStorage.getItem('portalLang')).lang : '';
        }

        if (!categoryId || categoryId === null) {
            categoryId = '0';
        }
        this.change.emit(categoryId);
        if (isPortal) {
            if (this.saveFilteringParams) {
                sessionStorage.setItem(`selectedValue${currentPortal}${lang}`, categoryId);
            }
            sessionStorage.removeItem(`selectedSubCatValue${currentPortal}${lang}`);
            sessionStorage.removeItem(`selectedSubSubCatValue${currentPortal}${lang}`);
        } else {
            if (this.saveFilteringParams) {
                sessionStorage.setItem(`selectedValue${_site}`, categoryId);
            }
            sessionStorage.removeItem(`selectedSubCatValue${_site}`);
            sessionStorage.removeItem(`selectedSubSubCatValue${_site}`);
        }
        this.setSubCats(categoryId);
        this.setSubSubCats();
    }

    setSubCats(categoryId) {
        const newFirstElement = { id: 0, name: this.label };
        const noValueElement = { id: 0, name: this.labelNoValue };

        let category;
        if (this._$categories.getValue()) {
            category = this._$categories.getValue().filter((f) => f.id.toString().toLowerCase() === categoryId.toLowerCase())[0];
            if (category && category.childCategories && category.childCategories.length > 0) {
                const filteredCategories = [newFirstElement].concat(category.childCategories);
                this._$subCategories.next([...filteredCategories]);
                this._$subSubCategories.next([newFirstElement]);
            } else {
                this._$subCategories.next([noValueElement]);
                this._$subSubCategories.next([noValueElement]);
                this.selectedValueSubCat = this.labelNoValue;
                this.selectedValueSubSubCat = this.labelNoValue;
            }
        }
    }

    onSelectSubCat(categoryId: string, reset: boolean) {
        const _site = this.config.getCurrentSite();
        const currentPortal = this.config.portalId;
        const isPortal = this.config.isPartnerPage();
        const lang =
            JSON.parse(sessionStorage.getItem('portalLang')) && JSON.parse(sessionStorage.getItem('portalLang')).lang
                ? JSON.parse(sessionStorage.getItem('portalLang')).lang
                : '';

        this.changeSubCat.emit(categoryId);

        if (isPortal) {
            this.onSelect(sessionStorage.getItem(`selectedValue${currentPortal}${lang}`), false);
            if (this.saveFilteringParams) {
                sessionStorage.setItem(`selectedSubCatValue${currentPortal}${lang}`, categoryId);
            }
            sessionStorage.removeItem(`selectedSubSubCatValue${currentPortal}${lang}`);
        } else {
            this.onSelect(sessionStorage.getItem(`selectedValue${_site}`), false);
            if (this.saveFilteringParams) {
                sessionStorage.setItem(`selectedSubCatValue${_site}`, categoryId);
            }
            sessionStorage.removeItem(`selectedSubSubCatValue${_site}`);
        }

        this.setSubSubCats();
    }

    setSubSubCats() {
        const newFirstElement = { id: this.label, name: this.label };
        const noValueElement = { id: 0, name: this.labelNoValue };

        let cats;
        let subCats;
        if (this._$categories.getValue()) {
            cats = this._$categories.getValue().filter((f) => f.id === this.selectedValue)[0];
            if (cats && cats.childCategories) {
                subCats = cats.childCategories.filter((f) => f.name === this.selectedValueSubCat)[0];
                if (subCats && subCats.childCategories && subCats.childCategories.length > 0) {
                    const filteredCategories = [newFirstElement].concat(subCats.childCategories);
                    this._$subSubCategories.next([...filteredCategories]);
                } else {
                    if (this.selectedValueSubCat === this.labelNoValue || this.selectedValueSubCat === this.label) {
                        this._$subSubCategories.next([noValueElement]);
                        this.selectedValueSubSubCat = this.labelNoValue;
                    } else {
                        this._$subSubCategories.next([newFirstElement]);
                        this.selectedValueSubSubCat = this.label;
                    }
                }
            }
        }
    }

    onSelectSubSubCat(categoryId: string) {
        const _site = this.config.getCurrentSite();
        const currentPortal = this.config.portalId;
        const isPortal = this.config.isPartnerPage();
        const lang =
            JSON.parse(sessionStorage.getItem('portalLang')) && JSON.parse(sessionStorage.getItem('portalLang')).lang
                ? JSON.parse(sessionStorage.getItem('portalLang')).lang
                : '';

        if (isPortal) {
            this.onSelectSubCat(sessionStorage.getItem(`selectedSubCatValue${currentPortal}${lang}`), false);
            if (this.saveFilteringParams) {
                sessionStorage.setItem(`selectedSubSubCatValue${currentPortal}${lang}`, categoryId);
            }
        } else {
            this.onSelectSubCat(sessionStorage.getItem(`selectedSubCatValue${_site}`), false);
            if (this.saveFilteringParams) {
                sessionStorage.setItem(`selectedSubSubCatValue${_site}`, categoryId);
            }
        }

        this.changeSubSubCat.emit(categoryId);
    }

    public getCategories() {
        if (this.saveFilteringParams) {
            const _selectedValueFromStorage = !this.selectedValue || this.selectedValue === 'null';
            const _selectedValueFromStorageSubCat =
                !this.selectedValueSubCat || this.selectedValueSubCat === 'null' || this.selectedValueSubCat === '0';
            const _selectedValueFromStorageSubSubCat =
                !this.selectedValueSubSubCat || this.selectedValueSubSubCat === 'null' || this.selectedValueSubSubCat === '0';

            if (!_selectedValueFromStorage && _selectedValueFromStorageSubCat && _selectedValueFromStorageSubSubCat) {
                this.onSelect(this.selectedValue, false);
            } else if (!_selectedValueFromStorage && !_selectedValueFromStorageSubCat && _selectedValueFromStorageSubSubCat) {
                this.onSelectSubCat(this.selectedValueSubCat, false);
            } else if (!_selectedValueFromStorage && !_selectedValueFromStorageSubCat && !_selectedValueFromStorageSubSubCat) {
                this.onSelectSubSubCat(this.selectedValueSubSubCat);
            } else {
                if (this._$categories.getValue()) {
                    if (this._$categories.getValue().length === 1 && this._$categories.getValue()[0]) {
                        this.selectedValue = this._$categories.getValue()[0].id;
                        this.onSelect(this._$categories.getValue()[0].id, false);
                    } else {
                        this.onSelect('0', false);
                    }
                }
            }
        } else {
            this.onSelect('0', false);
            this.selectedValue = '0';
        }
    }

    public removeParam(parameter) {
        let url = document.location.href;
        const urlparts = url.split('?');

        if (urlparts.length >= 2) {
            const urlBase = urlparts.shift();
            const queryString = urlparts.join('?');

            const prefix = encodeURIComponent(parameter) + '=';
            const pars = queryString.split(/[&;]/g);
            for (let i = pars.length; i-- > 0; ) {
                if (pars[i].lastIndexOf(prefix, 0) !== -1) {
                    pars.splice(i, 1);
                }
            }
            url = urlBase + '?' + pars.join('&');
            window.history.pushState('', document.title, url); // added this line to push the new url directly to url bar .
            window.history.pushState('', document.title, urlBase);
        }
        return url;
    }
    public getLabelsForAllOrNothing() {
        this._translateService.stream('common').subscribe((data) => {
            if (data) {
                this._$translations.next(data);
                if (this._$translations.getValue().filteringDropdownAll) {
                    this.label = this._$translations.getValue().filteringDropdownAll;
                } else {
                    this.label = 'All';
                }
                if (this._$translations.getValue().filteringDropdownNoSelection) {
                    this.labelNoValue = this._$translations.getValue().filteringDropdownNoSelection;
                } else {
                    this.labelNoValue = 'No selected value';
                }
                if (this._$translations.getValue().filteringDropdownAll) {
                    let categoryId = '0';
                    let lang = '';
                    if (JSON.parse(sessionStorage.getItem('portalLang'))) {
                        lang = JSON.parse(sessionStorage.getItem('portalLang')).lang
                            ? JSON.parse(sessionStorage.getItem('portalLang')).lang
                            : '';
                    }
                    const _site = this.config.getCurrentSite();
                    const currentPortal = this.config.portalId;
                    if (sessionStorage.getItem(`selectedValue${currentPortal}${lang}`)) {
                        categoryId = sessionStorage.getItem(`selectedValue${currentPortal}${lang}`);
                    }
                    if (sessionStorage.getItem(`selectedValue${_site}${lang}`)) {
                        categoryId = sessionStorage.getItem(`selectedValue${_site}${lang}`);
                    }

                    this.setSubCats(categoryId);
                    this.setSubSubCats();
                }
            }
        });
    }

    public bussinesFieldDisabled(isPortal) {
        if (this._$categories.getValue()) {
            if (this._$categories.getValue().length === 1) {
                this._$disablingBusinessField.next(true);
            } else {
                this._$disablingBusinessField.next(false);
            }
        }
    }
}
